import { useState } from "react";
import { Link } from "react-router-dom";
import { Hash, Plus, BookOpenCheck, Mail, Trophy, Lock, Target, ChevronDown, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n";

/**
 * Compact "quick rules" card. Variants:
 *   - landing: full width, used on /landing (logged-out hero followup)
 *   - dashboard: condensed, shows above pool grid for logged-in users
 *
 * Content stays in sync between the two — only padding + bullet count differ.
 */
export default function QuickRulesCard({ variant = "dashboard", onJoinClick, onCreateClick, collapsible = false, defaultOpen = true }) {
    const { t } = useI18n();
    const [open, setOpen] = useState(defaultOpen);
    const items = [
        { icon: Trophy, key: "predict" },
        { icon: Lock, key: "lock" },
        { icon: Target, key: "bonus" },
    ];

    const isLanding = variant === "landing";

    return (
        <div
            data-testid={`quickrules-${variant}`}
            className={`rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 ${isLanding ? "p-6 sm:p-8" : "p-4 sm:p-5"} mb-6`}
        >
            <button
                type="button"
                onClick={collapsible ? () => setOpen((v) => !v) : undefined}
                disabled={!collapsible}
                data-testid={`quickrules-${variant}-toggle`}
                className={`w-full flex items-center gap-2 ${open && collapsible ? "mb-3" : "mb-0"} ${collapsible ? "cursor-pointer" : "cursor-default"}`}
            >
                {collapsible && (open
                    ? <ChevronDown className="h-4 w-4 text-[var(--accent-hex)]" />
                    : <ChevronRight className="h-4 w-4 text-[var(--accent-hex)]" />)}
                <BookOpenCheck className="h-5 w-5 text-[var(--accent-hex)]" />
                <h2 className={`font-heading ${isLanding ? "text-2xl" : "text-lg"} font-extrabold uppercase tracking-tight text-[var(--text-primary)]`}>
                    {t("quickrules.title")}
                </h2>
            </button>

            {open && (
                <>
                    <ul className="grid sm:grid-cols-3 gap-3 mb-4">
                {items.map((it) => {
                    const Icon = it.icon;
                    return (
                        <li key={it.key} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                            <Icon className="h-4 w-4 text-[var(--accent-hex)] flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-bold text-[var(--text-primary)]">{t(`quickrules.${it.key}.t`)}</div>
                                <div className="text-xs">{t(`quickrules.${it.key}.d`)}</div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* CTAs: Join with code + Create — same call-to-actions, different
                handlers between landing (links to /register) and dashboard (modal triggers). */}
            <div className="flex flex-wrap items-center gap-2">
                {isLanding ? (
                    <>
                        <Link to="/register" data-testid="quickrules-register"
                            className="inline-flex items-center gap-1.5 bg-[var(--accent-hex)] text-[var(--accent-text)] text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded hover:bg-[var(--accent-hex-hover)] transition-colors">
                            <Plus className="h-3.5 w-3.5" /> {t("quickrules.cta.create")}
                        </Link>
                        <Link to="/login" data-testid="quickrules-join"
                            className="inline-flex items-center gap-1.5 border border-[var(--border-hover)] text-[var(--text-primary)] text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded hover:border-white/60 transition-colors">
                            <Hash className="h-3.5 w-3.5" /> {t("quickrules.cta.join")}
                        </Link>
                    </>
                ) : (
                    <>
                        <button type="button" onClick={onCreateClick} data-testid="quickrules-create"
                            className="inline-flex items-center gap-1.5 bg-[var(--accent-hex)] text-[var(--accent-text)] text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded hover:bg-[var(--accent-hex-hover)] transition-colors">
                            <Plus className="h-3.5 w-3.5" /> {t("quickrules.cta.create")}
                        </button>
                        <button type="button" onClick={onJoinClick} data-testid="quickrules-join"
                            className="inline-flex items-center gap-1.5 border border-[var(--border-hover)] text-[var(--text-primary)] text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded hover:border-white/60 transition-colors">
                            <Hash className="h-3.5 w-3.5" /> {t("quickrules.cta.join")}
                        </button>
                    </>
                )}
                <Link to="/help" data-testid="quickrules-help"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[var(--accent-hex)] hover:underline ml-1">
                    {t("quickrules.cta.full")}
                </Link>
                <a href="mailto:ola.wollin@gmail.com" data-testid="quickrules-contact"
                    className="inline-flex items-center gap-1 ml-auto text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                    <Mail className="h-3 w-3" /> {t("quickrules.contact")}
                </a>
            </div>
                </>
            )}
        </div>
    );
}
